import type { MatterTable } from '../schemas'
import { RuntimeFeatureFlag } from '@/feature-flags/definitions'
import { useRuntimeFeatureFlag } from '@/feature-flags/useRuntimeFeatureFlag'
import { isValidUuid } from '@/util/is-valid-uuid'
import { putTableChat } from '@/tables/fetch/put-table-chat'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { matterPaths } from '@/routing/paths'
import { useAppSelector } from '@/store/store-hooks'
import { selectAgentV2Transcript } from '@/agent-v2/store/selectors'

/** History-entry state distinguishes chat navigation from opening the table's assistant. */
export type TableNavigationState = { originatingConversationId?: string; initialTable?: MatterTable; openAssistant?: boolean }

/** Links the standalone table's chat and opens newly created tables after the active response completes. */
export function useChatTable(conversationId: string, standaloneTableId?: string) {
  const { enabled, loading } = useRuntimeFeatureFlag(RuntimeFeatureFlag.TABLES)
  const tablesEnabled = enabled && !loading
  const { matterId } = useParams()
  const navigate = useNavigate()
  const transcript = useAppSelector((state) => selectAgentV2Transcript(state, { conversationId }))
  const activeMessage = useRef<string | null>(null)
  const tableId = tablesEnabled ? standaloneTableId : null
  const [linkError, setLinkError] = useState<string | null>(null)
  useEffect(() => {
    setLinkError(null)
    if (!tableId || !isValidUuid(tableId) || !isValidUuid(conversationId)) return
    const controller = new AbortController()
    putTableChat(tableId, conversationId, controller.signal).catch((error: unknown) => {
      if (!controller.signal.aborted) setLinkError(error instanceof Error ? error.message : 'Unable to link table to chat.')
    })
    return () => controller.abort()
  }, [tableId, conversationId])
  const openTable = useCallback(
    (id: string) => {
      if (tablesEnabled && matterId)
        navigate(`${matterPaths.table(matterId, id)}?chat=${conversationId}`, {
          state: { originatingConversationId: conversationId } satisfies TableNavigationState,
        })
    },
    [conversationId, matterId, navigate, tablesEnabled]
  )
  useEffect(() => {
    if (!tablesEnabled) {
      activeMessage.current = null
      return
    }
    if (transcript.activeAssistantMessageId) activeMessage.current = transcript.activeAssistantMessageId
    if (transcript.activeAssistantMessageId || !activeMessage.current) return
    const message = transcript.messages.find((item) => item.id === activeMessage.current)
    activeMessage.current = null
    if (!message || !matterId) return
    const createdTableId = message.createdTableIds?.[0]
    if (createdTableId) openTable(createdTableId)
  }, [transcript.activeAssistantMessageId, transcript.messages, matterId, openTable, tablesEnabled])
  return {
    linkError,
    tableId: tableId && isValidUuid(tableId) ? tableId : null,
    openTable,
    viewRefreshKey: tablesEnabled
      ? transcript.messages
          .map((message) => message.tableViewUpdateIds?.[standaloneTableId ?? ''])
          .filter(Boolean)
          .join(',')
      : '',
    refreshKey: transcript.messages
      .filter((message) => message.role === 'assistant' && !message.isStreaming)
      .map((message) => message.id)
      .join(','),
  }
}
